import React, { useMemo } from 'react';
import { TagColored } from '@spark';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';
import './versionsDiff.css';

const spanKey = (span) => `${span.start_index}|${span.content}|${span.label}`;

/**
 * One version's example text with its entity spans highlighted inline in the
 * label's tag color (Record Diff modal, "Changed Entities" tab). Spans absent
 * from the other version (`otherSpans`) are underlined as new.
 */
const EntityHighlightedText = ({ text = '', entities = [], otherSpans = [], allLabels = [] }) => {
  const otherKeys = useMemo(() => new Set(otherSpans.map(spanKey)), [otherSpans]);

  const parts = useMemo(() => {
    const sorted = [...entities]
      .filter((span) => Number.isFinite(span.start_index) && span.content)
      .sort((a, b) => a.start_index - b.start_index);

    const result = [];
    let cursor = 0;
    for (const span of sorted) {
      const start = span.start_index;
      const end = start + span.content.length;
      // Skip malformed / overlapping spans rather than corrupting the text.
      if (start < cursor || start > text.length || text.slice(start, end) !== span.content) {
        continue;
      }
      if (start > cursor) {
        result.push({ type: 'text', text: text.slice(cursor, start) });
      }
      result.push({ type: 'entity', span });
      cursor = end;
    }
    if (cursor < text.length) {
      result.push({ type: 'text', text: text.slice(cursor) });
    }
    return result;
  }, [text, entities]);

  return (
    <span className="vdm-entity-text">
      {parts.map((part, index) =>
        part.type === 'text' ? (
          <React.Fragment key={index}>{part.text}</React.Fragment>
        ) : (
          <TagColored
            key={index}
            noPadding
            variant={getSparkTagVariant(part.span.label, allLabels)}
            label={part.span.content}
            className={otherKeys.has(spanKey(part.span)) ? undefined : 'vdm-entity-new'}
          />
        ),
      )}
    </span>
  );
};

export default EntityHighlightedText;
